<template>
  <div v-on-click-outside="hideMenu" class="relative flex">
    <div
      class="relative flex flex-1 flex-col"
      :class="[isDisabled ? 'bg-gray-50' : 'bg-inherit']"
    >
      <span
        v-show="displayText && !isIcon"
        class="absolute z-10 -mt-3 ml-2-5 w-min px-1-25 text-xs whitespace-nowrap"
        :class="[
          isDisabled ? 'bg-gray-50' : 'bg-white',
          showMenu ? 'text-blue-500' : 'text-gray-400',
        ]"
      >
        {{ $t("COMMON.MONTHS") }}
      </span>
      <button
        v-if="!isIcon"
        type="button"
        data-cy="month-picker-toggle-button"
        class="relative h-12 w-full rounded-sm py-1 pr-8 pl-4 text-left text-sm hover:border-gray-300"
        :class="buttonPickerStyle"
        :disabled="isDisabled"
        @click.stop="toggleMenu"
      >
        <span
          class="dropdown-icon absolute inset-y-0 right-0 ml-3 flex cursor-pointer items-center pr-2"
        >
          <icon-base width="10" height="7" :icon="IconChevronUp" />
        </span>
        {{ displayText }}
      </button>
      <div v-else v-tooltip="{ content: displayText }">
        <slot name="icon-activator" :toggle-menu="toggleMenu">
          <button
            type="button"
            class="relative w-full rounded-sm py-1 text-left text-sm hover:border-gray-300"
            :class="buttonPickerStyle"
            :disabled="isDisabled"
            @click.stop="toggleMenu"
          >
            <icon-base
              width="16"
              height="16"
              :icon="IconCalendar"
              :icon-color="iconCalendarStyle"
            />
          </button>
        </slot>
      </div>

      <div v-show="showMenu" class="-bottom-12 h-full">
        <div
          class="absolute -right-5 z-50 min-w-max space-y-2 overflow-y-auto rounded-md bg-white py-5 pt-2 text-sm text-gray-500 shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
          :class="monthPickerMenuClass"
          data-cy="month-picker"
        >
          <slot name="extra" />
          <template v-if="showViewTypes && !onlyDatePicker">
            <lf-switch
              name="viewType"
              class="w-fit-content flex-none px-4"
              :model-value="isLookbackRangeSelection"
              data-cy="month-picker-range-switch"
              @toggle-changed="toggleLookbackViewType"
            >
              <span class="mr-2 text-headline">
                {{ currentSelectionType }}
              </span>
            </lf-switch>
          </template>
          <h5
            v-if="!onlyDatePicker"
            class="mb-3 px-4 text-xs font-medium text-gray-500 uppercase"
          >
            {{ $t("ORCHESTRATION.SELECTED_MONTHS") }}
          </h5>
          <div v-if="!onlyDatePicker" class="grid grid-cols-6 gap-2 px-4">
            <div
              v-for="month in MONTHS"
              :key="month.monthNum"
              :data-cy="`month-${month.monthNum}`"
              class="flex h-10 w-10 cursor-pointer items-center justify-center rounded-4 border border-gray-200 text-xs font-semibold uppercase"
              :class="[
                {
                  'bg-primary text-white': isMonthSelected(month.monthNum),
                },
                { 'text-disabled-medium': !isMonthSelected(month.monthNum) },
              ]"
              @click="setLookback(month.monthNum)"
            >
              {{ month.monthNum }}
            </div>
            <div
              class="col-span-3 flex h-10 cursor-pointer items-center justify-center rounded-4 border border-gray-200 text-xs font-semibold uppercase"
              :class="[
                {
                  'bg-primary text-white': showMonthPicker,
                },
                { 'text-disabled-medium': !showMonthPicker },
              ]"
              @click="openRangeSelector"
            >
              {{ $t("ORCHESTRATION.CUSTOM_SELECTION") }}
            </div>
            <div
              class="col-span-3 flex h-10 cursor-pointer items-center justify-center rounded-4 border border-gray-200 text-xs font-semibold uppercase"
              :class="[
                {
                  'bg-primary text-white': hasMonthToDate,
                },
                { 'text-disabled-medium': !hasMonthToDate },
              ]"
              @click="handleMonthToDateChange"
            >
              {{ $t("ORCHESTRATION.MONTH_TO_DATE") }}
            </div>
          </div>
          <lf-month-picker
            v-if="showMonthPicker || onlyDatePicker"
            v-model="customMonthsRange"
            show-selector
            :is-date-range="isDateRange"
            embedded
            class="mt-5 border-t border-gray-200 px-4 pt-3"
            date-format="yyyy-MM-dd"
          />
          <div
            v-if="errorPresent && !showMonthPicker && !isLookbackRangeSelection"
            class="px-4 text-red-500"
          >
            {{ $t("ORCHESTRATION.MONTHPICKER_ERROR_MONTH") }}
          </div>
          <div v-else-if="errorPresent" class="px-4 text-red-500">
            {{ $t("ORCHESTRATION.MONTHPICKER_ERROR") }}
          </div>
          <div
            v-if="!isIcon && !onlyDatePicker"
            class="mt-5 flex justify-end px-4"
          >
            <outline-button class="mr-2 w-1/2" @click="handleSubmit(true)">
              {{ $t("ORCHESTRATION.APPLY_TO_ALL") }}
            </outline-button>
            <primary-button class="w-1/2" @click="handleSubmit(false)">
              {{ $t("COMMON.APPLY") }}
            </primary-button>
          </div>
          <div v-else class="mt-4 border-t p-4">
            <primary-button class="w-full" @click="handleSubmit(false)">
              {{
                !onlyDatePicker
                  ? $t("COMMON.APPLY")
                  : $t("COMMON.SELECT_PERIOD")
              }}
            </primary-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { MONTHS, UTIL_COLORS } from "@/helpers/constants";

import IconCalendar from "@/components/icons/IconCalendar.vue";
import IconChevronUp from "@/components/icons/deals/IconChevronUp.vue";
import LfMonthPicker from "@/components/ui/inputs/LfMonthPicker.vue";

import type { LookbackValue } from "@/models/orchestration";
import type { PropType } from "vue";

const props = defineProps({
  value: {
    type: Array as PropType<string[] | number[]>,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isIcon: {
    type: Boolean,
    default: false,
  },
  isDateRange: {
    type: Boolean,
    default: true,
  },
  onlyDatePicker: {
    type: Boolean,
    default: false,
  },
  showViewTypes: {
    type: Boolean,
    default: true,
  },
  monthPickerMenuClass: {
    type: String,
    default: "max-h-130",
  },
});

const { t } = useI18n();

const emit = defineEmits<{
  (eventName: "context:change", option: LookbackValue): void;
  (eventName: "context:update-all", option: LookbackValue): void;
}>();

const lookbackMonthsRange = ref<number[]>([]);

const showMonthPicker = ref(false);

const errorPresent = ref(false);

const customMonthsRange = ref<string[]>([]);

const showMenu = ref(false);

const isLookbackRangeSelection = ref(false);

const hasMonthToDate = ref(false);

const valueIsNumbers = computed(
  () =>
    typeof lookbackMonthsRange.value[0] === "number" &&
    typeof lookbackMonthsRange.value?.[1] === "number"
);

const valueIsLookbackMonth = computed(
  () =>
    valueIsNumbers.value &&
    lookbackMonthsRange.value?.[0] === lookbackMonthsRange.value?.[1] &&
    !isLookbackRangeSelection.value
);

const currentSelectionType = computed(() =>
  isLookbackRangeSelection.value
    ? t("ORCHESTRATION.SELECT_RANGE")
    : t("ORCHESTRATION.SELECT_MONTH")
);

const buttonPickerStyle = computed(() => {
  if (props.isIcon) {
    let iconStyle = "p-4 h-10";
    if (showMenu.value) {
      iconStyle = `border-2 border-gray-500 ${iconStyle}`;
    } else {
      iconStyle = `${iconStyle} border`;
    }
    return !!displayText.value?.length && !showMenu.value
      ? `${iconStyle} border-blue-500 hover:border-blue-500`
      : `${iconStyle} border`;
  } else {
    let textStyle = "pl-4 pr-8 h-12";
    if (showMenu.value) {
      textStyle = `${textStyle} border-blue-500 hover:border-blue-500 border-2`;
    } else {
      textStyle = `${textStyle} border`;
    }
    return props.isDisabled || !displayText.value
      ? `${textStyle} text-gray-400`
      : `${textStyle} text-headline`;
  }
});

const isMonthToDateOnly = computed(() =>
  lookbackMonthsRange.value.every((value) => value === 0)
);

const iconCalendarStyle = computed(() => {
  if (showMenu.value) {
    return "#868D9C";
  }
  return props.value?.length ? UTIL_COLORS.PRIMARY : UTIL_COLORS.DEFAULT;
});

const displayText = computed(() => {
  if (hasMonthToDate.value) {
    if (valueIsLookbackMonth.value) {
      return `${t("ORCHESTRATION.MONTH_TO_DATE")}, ${t("COMMON.MONTH")}`;
    }
    if (lookbackMonthsRange.value.length) {
      return `${t("ORCHESTRATION.MONTH_TO_DATE")}, ${t("COMMON.MONTHS")}`;
    }
    return t("ORCHESTRATION.MONTH_TO_DATE");
  }

  if (lookbackMonthsRange.value) {
    return t("COMMON.MONTHS");
  }

  if (valueIsLookbackMonth.value) {
    return t("COMMON.MONTH");
  }

  if (props.isDateRange && !props.onlyDatePicker) {
    return t("COMMON.MONTHS");
  }

  if (customMonthsRange.value.length) {
    const startDate = customMonthsRange.value[0].split("-");
    if (props.isDateRange) {
      const endDate = customMonthsRange.value[1].split("-");
      return `${startDate[1]}/${startDate[0]} - ${endDate[1]}/${endDate[0]}`;
    }
    return `${startDate[1]}/${startDate[0]}`;
  }

  return t("COMMON.MONTHS");
});

const toggleLookbackViewType = (_: boolean, e: Event) => {
  e.preventDefault();
  isLookbackRangeSelection.value = !isLookbackRangeSelection.value;
  hasMonthToDate.value = false;
  lookbackMonthsRange.value = [];
  showMonthPicker.value = false;
  customMonthsRange.value = [];
};

const handleMonthToDateChange = () => {
  hasMonthToDate.value = !hasMonthToDate.value;
  showMonthPicker.value = false;
  customMonthsRange.value = [];

  if (valueIsLookbackMonth.value || !lookbackMonthsRange.value.length) {
    lookbackMonthsRange.value = [0, 0];
    return;
  }

  const max = Math.max(...lookbackMonthsRange.value);
  lookbackMonthsRange.value = [max, 0];
};

const isMonthSelected = (month: number) => {
  // if range selection, return true if month is in range
  if (lookbackMonthsRange.value.length < 2) {
    return false;
  }

  return (
    lookbackMonthsRange.value[1] <= month &&
    month <= lookbackMonthsRange.value[0] &&
    !showMonthPicker.value
  );
};

const setLookback = (month: number) => {
  hasMonthToDate.value = false;
  showMonthPicker.value = false;
  customMonthsRange.value = [];

  if (!lookbackMonthsRange.value.length) {
    lookbackMonthsRange.value = [month, month];
    return;
  }

  if (isMonthToDateOnly.value) {
    lookbackMonthsRange.value = [month, 0];
    return;
  }

  if (isMonthToDateOnly.value || !isLookbackRangeSelection.value) {
    lookbackMonthsRange.value = [month, month];
    return;
  }

  if (month < lookbackMonthsRange.value[1]) {
    lookbackMonthsRange.value = [lookbackMonthsRange.value[0], month];
    return;
  }

  if (
    month >= lookbackMonthsRange.value[1] &&
    month <= lookbackMonthsRange.value[0]
  ) {
    lookbackMonthsRange.value = [month, month];
    return;
  }

  lookbackMonthsRange.value = [month, lookbackMonthsRange.value[1]];
};

const openRangeSelector = () => {
  lookbackMonthsRange.value = [];
  hasMonthToDate.value = false;
  showMonthPicker.value = true;
};

const handleSubmit = (all = false) => {
  if (
    props.isDateRange &&
    showMonthPicker.value &&
    customMonthsRange.value.length < 2
  ) {
    errorPresent.value = true;
    return;
  }

  // if lookback is available, and it is a range selection, throw error if less than 2 months are selected
  if (isLookbackRangeSelection.value && valueIsLookbackMonth.value) {
    errorPresent.value = true;
    return;
  }

  // if lookback is available, and it is not a range selection, throw error if a lookback month is not selected
  if (valueIsLookbackMonth.value && !lookbackMonthsRange.value.length) {
    errorPresent.value = true;
    return;
  }

  if (!lookbackMonthsRange.value.length && !customMonthsRange.value.length) {
    errorPresent.value = true;
    return;
  }

  emitValue(all);
  showMenu.value = false;
};

const emitValue = (all: boolean) => {
  if (customMonthsRange.value.length) {
    if (all) {
      emit("context:update-all", {
        lookback: customMonthsRange.value,
        hasMonthToDate: hasMonthToDate.value,
      });
      return;
    }

    emit("context:change", {
      lookback: customMonthsRange.value,
      hasMonthToDate: hasMonthToDate.value,
    });

    return;
  }

  if (all) {
    emit("context:update-all", {
      lookback: lookbackMonthsRange.value,
      hasMonthToDate: hasMonthToDate.value,
      type: "relative_lookback_range",
    });
    return;
  }

  emit("context:change", {
    lookback: lookbackMonthsRange.value,
    hasMonthToDate: hasMonthToDate.value,
    type: "relative_lookback_range",
  });

  return;
};

const toggleMenu = () => {
  if (showMenu.value) {
    hideMenu();
  } else {
    showMenu.value = true;
  }
};

const hideMenu = () => {
  if (props.value?.length === 2 && !isLookbackRangeSelection.value) {
    customMonthsRange.value = props.value as string[];
  }

  showMenu.value = false;
};

watch(
  () => props.value,
  (newValue) => {
    const isLookbackValue =
      typeof newValue?.[0] === "number" && typeof newValue?.[1] === "number";

    if (isLookbackValue) {
      lookbackMonthsRange.value = newValue as number[];
      isLookbackRangeSelection.value = !valueIsLookbackMonth.value;
      hasMonthToDate.value = newValue?.some((val) => val === 0) || false;
      showMonthPicker.value = false;
      customMonthsRange.value = [];
      return;
    }

    isLookbackRangeSelection.value = false;
    lookbackMonthsRange.value = [];
    customMonthsRange.value = newValue as string[];
    showMonthPicker.value = true;
  },
  { immediate: true }
);

watch(customMonthsRange, () => {
  errorPresent.value = false;
});

watch(lookbackMonthsRange, (range) => {
  if (range.includes(0)) {
    hasMonthToDate.value = true;
  }
});
</script>
